import { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { apiClient } from '../api/client';
import { adminAPI, type AuditEntry } from '../api/admin';
import { useAuthStore } from '../store/authStore';
import { useSignOut } from '../hooks/useSignOut';
import { analytics } from '../hooks/useAnalytics';
import { BottomNav } from '../components/ui/BottomNav';
import { LanguagePicker } from '../components/ui/LanguagePicker';
import { useTranslation } from '../i18n';
import { getDirectionalArrow } from '../utils/arrows';
import { STALE_TIMES } from '../constants/staleTimes';
import { openExternalUrl } from '../lib/externalLink';

const TIMEZONES = [
  'UTC',
  'Asia/Jerusalem',
  'Europe/London',
  'America/New_York',
  'America/Los_Angeles',
  'Europe/Paris',
];

// ─── types ────────────────────────────────────────────────────────────────────

interface AdminUser {
  id: string;
  name: string;
  apartment: string;
  email: string;
  phone: string | null;
  is_admin: boolean;
  spot_count: number;
  joined_at: string;
}

interface InviteCode {
  id: string;
  code: string;
  expires_at: string | null;
  max_uses: number | null;
  use_count: number;
  is_active: boolean;
  created_at: string;
}

// AuditEntry type imported from api/admin module above

interface BuildingLevel {
  id: string;
  name: string;
  code: string;
  sort_order: number;
  is_active: boolean;
  spot_count: number;
}

// ─── helpers ──────────────────────────────────────────────────────────────────

function formatEventType(t: string | undefined) {
  if (!t) return '—';
  return t.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function getInitials(name: string) {
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

function safeFormat(dateStr: string | null | undefined, formatStr: string) {
  if (!dateStr || typeof dateStr !== 'string' || dateStr.trim() === '') return '—';
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '—';
    return format(date, formatStr);
  } catch {
    return '—';
  }
}

const AVATAR_COLORS = [
  ['#dbeafe', '#1d4ed8'],
  ['#f3e8ff', '#7c3aed'],
  ['#fce7f3', '#be185d'],
  ['#d1fae5', '#065f46'],
  ['#fef3c7', '#92400e'],
];

// ─── AdminPage ────────────────────────────────────────────────────────────────

export default function AdminPage() {
  const navigate = useNavigate();
  const qc       = useQueryClient();
  const user     = useAuthStore((s) => s.user);
  const isAdmin  = useAuthStore((s) => s.user?.is_admin ?? false);
  const signOut  = useSignOut();
  const { t, direction } = useTranslation();

  if (!isAdmin) {
    return <div style={{ padding: '20px', textAlign: 'center', marginTop: '40px' }}>{t('access_denied')}</div>;
  }

  const [userSearch,   setUserSearch]   = useState('');
  const [auditSearch,  setAuditSearch]  = useState('');
  const [auditHours,   setAuditHours]   = useState(24);
  const [copied,       setCopied]       = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  // ── danger zone state ──────────────────────────────────────────────────────
  const [buildingName,  setBuildingName]  = useState(user?.building.name ?? '');
  const [timezone,      setTimezone]      = useState(user?.building.timezone ?? 'UTC');
  const [nameSuccess,   setNameSuccess]   = useState(false);
  const [tzSuccess,     setTzSuccess]     = useState(false);

  // ── audit from timestamp ───────────────────────────────────────────────────

  const auditFrom = useMemo(
    () => new Date(Date.now() - auditHours * 3_600_000).toISOString(),
    [auditHours],
  );

  // ── queries ────────────────────────────────────────────────────────────────

  const levelsQ = useQuery({
    queryKey: ['admin', 'levels'],
    queryFn:  () => apiClient.get<{ data: BuildingLevel[] }>('/admin/levels').then((r) => r.data.data),
    enabled: !!user,
    staleTime: STALE_TIMES.STATIC,
  });

  const overviewCountQ = useQuery({
    queryKey: ['admin', 'users-count'],
    queryFn:  () =>
      apiClient
        .get<{ data: unknown; total: number }>('/admin/users?limit=1')
        .then((r) => r.data.total),
    enabled: !!user,
    staleTime: STALE_TIMES.SLOW,
  });

  const usersQ = useQuery({
    queryKey: ['admin', 'users', userSearch],
    queryFn:  () =>
      apiClient
        .get<{ data: AdminUser[]; total: number }>(
          `/admin/users?search=${encodeURIComponent(userSearch)}&limit=100`,
        )
        .then((r) => r.data),
    staleTime: STALE_TIMES.SLOW,
  });

  const codesQ = useQuery({
    queryKey: ['admin', 'invite-codes'],
    queryFn:  () =>
      apiClient.get<{ data: InviteCode[] }>('/admin/invite-codes').then((r) => r.data.data),
    staleTime: STALE_TIMES.STATIC,
  });

  const auditQ = useQuery({
    queryKey: ['admin', 'audit', auditFrom],
    queryFn:  async () => {
      // Fetch all audit logs from the specified time range using cursor pagination
      const allEntries: AuditEntry[] = [];
      let cursor: string | undefined;

      while (true) {
        const response = await adminAPI.getAuditLog(
          { from: auditFrom },
          50,
          cursor
        );
        allEntries.push(...response.data.data);

        if (!response.data.pagination.hasMore) break;
        cursor = response.data.pagination.nextCursor ?? undefined;
      }

      return { data: allEntries };
    },
    placeholderData: (prev) => prev,
  });

  // ── derived ────────────────────────────────────────────────────────────────

  const activeCodes  = (codesQ.data ?? []).filter((c) => c.is_active);
  const currentCode  = activeCodes[0] ?? null;

  const filteredAudit = useMemo(() => {
    const entries = auditQ.data?.data ?? [];
    if (!auditSearch.trim()) return entries;
    const q = auditSearch.toLowerCase();
    return entries.filter(
      (e) =>
        (e.actor?.apartment ?? '').toLowerCase().includes(q) ||
        formatEventType(e.eventType).toLowerCase().includes(q),
    );
  }, [auditQ.data, auditSearch]);

  // ── mutations ──────────────────────────────────────────────────────────────

  // ── danger zone mutations ──────────────────────────────────────────────────

  const patchNameMutation = useMutation({
    mutationFn: (name: string) =>
      apiClient.patch(`/admin/buildings/${user!.building.id}`, { name }),
    onSuccess: (_, name) => {
      useAuthStore.setState((s) => ({
        user: s.user ? { ...s.user, building: { ...s.user.building, name } } : null,
      }));
      setNameSuccess(true);
      setTimeout(() => setNameSuccess(false), 2000);
    },
  });

  const patchTzMutation = useMutation({
    mutationFn: (tz: string) =>
      apiClient.patch(`/admin/buildings/${user!.building.id}`, { timezone: tz }),
    onSuccess: (_, tz) => {
      useAuthStore.setState((s) => ({
        user: s.user ? { ...s.user, building: { ...s.user.building, timezone: tz } } : null,
      }));
      setTzSuccess(true);
      setTimeout(() => setTzSuccess(false), 2000);
    },
  });

  const createCodeMutation = useMutation({
    mutationFn: () => apiClient.post('/admin/invite-codes', {}),
    onSuccess:  () => {
      analytics.trackInviteCodeGenerated();
      void qc.invalidateQueries({ queryKey: ['admin', 'invite-codes'] });
    },
  });

  const revokeCodeMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/admin/invite-codes/${id}`),
    onSuccess:  () => void qc.invalidateQueries({ queryKey: ['admin', 'invite-codes'] }),
  });

  function copyCode() {
    if (!currentCode) return;
    void navigator.clipboard.writeText(currentCode.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  function shareWhatsApp() {
    if (!currentCode) return;
    const text = encodeURIComponent(
      `Join our building parking app! Use invite code: ${currentCode.code}`,
    );
    void openExternalUrl(`https://wa.me/?text=${text}`);
  }

  // ── render ─────────────────────────────────────────────────────────────────

  return (
    <>
      {/* Sign out confirmation dialog */}
      {showSignOutConfirm && (
        <>
          <div
            onClick={() => setShowSignOutConfirm(false)}
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0,0,0,0.45)',
              zIndex: 300,
            }}
          />
          <div style={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: 'calc(100% - 48px)',
            maxWidth: '320px',
            backgroundColor: '#ffffff',
            borderRadius: '20px',
            padding: '28px 20px 20px',
            zIndex: 301,
            boxShadow: '0 16px 40px -4px rgba(43,52,55,0.18)',
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '14px',
              background: '#fef2f2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 16px',
            }}>
              <span className="material-symbols-outlined" style={{ color: '#dc2626', fontSize: '24px' }}>logout</span>
            </div>
            <div style={{
              fontFamily: 'Manrope, system-ui',
              fontSize: '17px',
              fontWeight: 700,
              textAlign: 'center',
              marginBottom: '8px',
              color: '#2b3437',
            }}>
              {t('sign_out_confirm')}
            </div>
            <div style={{
              fontSize: '13px',
              color: '#6b7280',
              textAlign: 'center',
              marginBottom: '20px',
            }}>
              {t('sign_in_again')}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setShowSignOutConfirm(false)}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '12px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: '#ffffff',
                  color: '#2b3437',
                  fontWeight: 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                {t('cancel')}
              </button>
              <button
                onClick={() => signOut()}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #005bc0, #004fa9)',
                  color: '#ffffff',
                  fontWeight: 600,
                  fontSize: '14px',
                  cursor: 'pointer',
                }}
              >
                {t('sign_out')}
              </button>
            </div>
          </div>
        </>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh', maxWidth: '430px', margin: '0 auto', backgroundColor: '#f8f9fa', fontFamily: "'Inter', system-ui, sans-serif" }}>

      {/* ── Top bar ── */}
      <header style={{
        position: 'sticky',
        top: 0,
        zIndex: 50,
        height: '64px',
        padding: '0 24px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        backgroundColor: 'rgba(248,249,250,0.85)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        boxShadow: '0 12px 32px -4px rgba(43,52,55,0.06)',
      }}>
        <div style={{ width: '84px', flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          <LanguagePicker />
        </div>
        <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', pointerEvents: 'none' }}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 114 26" style={{ height: '26px', width: 'auto', display: 'block' }} aria-label="Lot'N'Go">
            <text x="57" y="20" fontFamily="Manrope, system-ui, sans-serif" fontWeight="700" fontSize="20" fill="#005bc0" textAnchor="middle" letterSpacing="-0.4">{"Lot'N'Go"}</text>
          </svg>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <button onClick={() => navigate('/profile')} style={iconBtn} title={t('settings')} aria-label={t('settings')}>
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>settings</span>
          </button>
          <button onClick={() => setShowSignOutConfirm(true)} style={iconBtn} title={t('sign_out')} aria-label={t('sign_out')}>
            <span className="material-symbols-outlined" style={{ fontSize: '22px' }}>logout</span>
          </button>
        </div>
      </header>

      {/* ── Page header ── */}
      <div style={{ padding: '20px 20px 0' }}>
        <h1 style={{ fontFamily: 'Manrope, system-ui', fontWeight: 800, fontSize: '28px', color: '#2b3437', marginBottom: '4px', letterSpacing: '-0.5px' }}>
          {t('admin_management')}
        </h1>
        <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '20px' }}>
          {t('manage_access_settings')}
        </p>
      </div>

      {/* ── Page content ── */}
      <div style={{ flex: 1, padding: '0 16px 16px', overflowY: 'auto' }}>

        {/* ───── System status ───── */}
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <span style={label}>{t('system_status')}</span>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', display: 'inline-block' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div style={statBox}>
              <div style={statNum}>{overviewCountQ.data ?? '—'}</div>
              <div style={statSub}>{t('residents')}</div>
            </div>
            <div style={statBox}>
              <div style={statNum}>{levelsQ.data?.filter((l) => l.is_active).length ?? '—'}</div>
              <div style={statSub}>{t('active_levels')}</div>
            </div>
          </div>
        </div>

        {/* ───── Parking Levels ───── */}
        <div style={{ ...card, padding: '16px 20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontFamily: 'Manrope, system-ui', fontWeight: 700, fontSize: '15px', color: '#2b3437' }}>{t('parking_levels')}</span>
            <button
              onClick={() => navigate('/admin/levels')}
              style={{ background: 'none', border: 'none', color: '#005bc0', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
            >
              {t('manage')} {getDirectionalArrow(direction)}
            </button>
          </div>
        </div>

        {/* ───── Users Section ───── */}
        <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '2px solid #f1f4f6' }}>
          <div style={sectionLabel}>{t('users')}</div>

          {/* Invite card — hidden */}
          {false && <div style={{
            background: 'linear-gradient(135deg, #005bc0 0%, #004fa9 100%)',
            borderRadius: '20px', padding: '20px', marginBottom: '16px',
            boxShadow: '0 4px 20px rgba(0,91,192,0.22)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <div style={{ width: '40px', height: '40px', borderRadius: '12px', backgroundColor: 'rgba(255,255,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <span className="material-symbols-outlined" style={{ color: '#fff', fontVariationSettings: "'FILL' 1" }}>person_add</span>
              </div>
              <div>
                <div style={{ fontFamily: 'Manrope, system-ui', fontWeight: 700, fontSize: '16px', color: '#fff' }}>{t('invite_resident')}</div>
                <div style={{ fontSize: '12px', color: 'rgba(255,255,255,0.65)' }}>{t('share_code_onboard')}</div>
              </div>
            </div>

            {currentCode && (
              <div style={{ backgroundColor: 'rgba(255,255,255,0.12)', borderRadius: '14px', padding: '14px 16px', marginBottom: '12px' }}>
                <div style={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.55)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>
                  {t('active_code')}
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontFamily: 'monospace', fontSize: '22px', fontWeight: 700, color: '#fff', letterSpacing: '3px' }}>
                    {currentCode.code}
                  </span>
                  <button
                    onClick={copyCode}
                    style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '8px', padding: '6px 12px', color: '#fff', fontSize: '12px', fontWeight: 700, cursor: 'pointer', flexShrink: 0 }}
                  >
                    {copied ? '✓ ' + t('copied') : t('copy')}
                  </button>
                </div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', marginTop: '6px' }}>
                  {t('used_times', { count: currentCode.use_count, max: currentCode.max_uses !== null ? `/${currentCode.max_uses}` : '' })}
                  {currentCode.expires_at && t('expires_at', { date: safeFormat(currentCode.expires_at, 'd MMM yyyy') })}
                </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={() => createCodeMutation.mutate()}
                disabled={createCodeMutation.isPending}
                style={{ flex: 1, padding: '12px', borderRadius: '12px', border: 'none', backgroundColor: '#fff', color: '#005bc0', fontWeight: 700, fontSize: '14px', cursor: 'pointer' }}
              >
                {createCodeMutation.isPending ? t('generating') : currentCode ? t('new_code') : t('generate_code')}
              </button>
              {currentCode && (
                <button
                  onClick={shareWhatsApp}
                  style={{ padding: '12px 16px', borderRadius: '12px', border: 'none', backgroundColor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 600, fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}
                >
                  <span className="material-symbols-outlined" style={{ fontSize: '18px', fontVariationSettings: "'FILL' 1" }}>share</span>
                  {t('share')}
                </button>
              )}
            </div>

            {/* Revoke older codes */}
            {activeCodes.length > 1 && (
              <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255,255,255,0.15)' }}>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginBottom: '6px' }}>{t('other_active_codes')}</div>
                {activeCodes.slice(1).map((c) => (
                  <div key={c.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontFamily: 'monospace', fontSize: '13px', color: 'rgba(255,255,255,0.7)', letterSpacing: '1px' }}>{c.code}</span>
                    <button
                      onClick={() => revokeCodeMutation.mutate(c.id)}
                      disabled={revokeCodeMutation.isPending}
                      style={{ background: 'none', border: 'none', color: 'rgba(255,100,100,0.9)', fontSize: '12px', fontWeight: 600, cursor: 'pointer' }}
                    >
                      {t('revoke')}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>}

          {/* Users list */}
          <div style={{ ...card, padding: 0, overflow: 'hidden' }}>
            <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f4f6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontFamily: 'Manrope, system-ui', fontWeight: 700, fontSize: '15px', color: '#2b3437' }}>
                  {t('residents')}{usersQ.data?.total ? ` (${usersQ.data.total})` : ''}
                </span>
              </div>
              <div style={{ position: 'relative' }}>
                <span className="material-symbols-outlined" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '18px', color: '#9ca3af', pointerEvents: 'none' }}>
                  search
                </span>
                <input
                  type="text"
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder={t('search_residents')}
                  style={{ width: '100%', height: '40px', paddingLeft: '38px', paddingRight: '12px', borderRadius: '10px', border: '1px solid #e5e7eb', fontSize: '14px', backgroundColor: '#f8f9fa', boxSizing: 'border-box', outline: 'none' }}
                />
              </div>
            </div>

            {usersQ.isLoading ? (
              <div style={emptyMsg}>{t('loading')}</div>
            ) : (usersQ.data?.data ?? []).length === 0 ? (
              <div style={emptyMsg}>{t('no_residents_found')}</div>
            ) : (
              (usersQ.data?.data ?? []).map((u, idx, arr) => {
                const [bg, fg] = AVATAR_COLORS[idx % AVATAR_COLORS.length];
                return (
                  <div
                    key={u.id}
                    style={{ padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px', borderBottom: idx < arr.length - 1 ? '1px solid #f1f4f6' : 'none' }}
                  >
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', flexShrink: 0, backgroundColor: bg, color: fg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: '14px' }}>
                      {getInitials(u.name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '14px', color: '#2b3437', marginBottom: '2px', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                        {u.name}
                        {u.is_admin && (
                          <span style={{ fontSize: '10px', fontWeight: 700, backgroundColor: '#fef3c7', color: '#92400e', padding: '1px 6px', borderRadius: '6px' }}>{t('admin')}</span>
                        )}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>
                      {u.phone && (
                        <div style={{ fontSize: '12px', color: '#9ca3af' }}>{u.phone}</div>
                      )}
                    </div>
                    <div style={{ flexShrink: 0, textAlign: 'right' }}>
                      <div style={{ fontSize: '11px', fontWeight: 700, color: u.spot_count > 0 ? '#004fa9' : '#9ca3af', backgroundColor: u.spot_count > 0 ? '#d8e2ff' : '#f1f4f6', padding: '3px 8px', borderRadius: '8px', marginBottom: '2px', whiteSpace: 'nowrap' }}>
                        {u.spot_count} {u.spot_count === 1 ? t('spot') : t('spots')}
                      </div>
                      <div style={{ fontSize: '11px', color: '#9ca3af' }}>{t('apt', { apartment: u.apartment })}</div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* ───── Audit Section ───── */}
        <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '2px solid #f1f4f6' }}>
          <div style={sectionLabel}>{t('audit_log')}</div>

          {/* Search */}
          <div style={{ position: 'relative', marginBottom: '12px' }}>
            <span className="material-symbols-outlined" style={{ position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', fontSize: '18px', color: '#9ca3af', pointerEvents: 'none' }}>
              search
            </span>
            <input
              type="text"
              value={auditSearch}
              onChange={(e) => setAuditSearch(e.target.value)}
              placeholder={t('search_audit')}
              style={{ width: '100%', height: '44px', paddingLeft: '42px', paddingRight: '14px', borderRadius: '12px', border: '1px solid #e5e7eb', fontSize: '14px', backgroundColor: '#fff', boxSizing: 'border-box', boxShadow: '0 2px 8px rgba(43,52,55,0.04)', outline: 'none' }}
            />
          </div>

          {/* Time range info */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <span style={label}>
              {t('last_hours', { hours: auditHours })}{auditSearch ? t('filtered') : ''}
            </span>
            {auditQ.data && (
              <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                {t('event_count', { count: filteredAudit.length, plural: filteredAudit.length !== 1 ? 's' : '' })}
              </span>
            )}
          </div>

          {/* Audit list */}
          <div style={{ ...card, padding: 0, overflow: 'auto', maxHeight: '400px', marginBottom: '12px' }}>
            {auditQ.isLoading ? (
              <div style={emptyMsg}>{t('loading')}</div>
            ) : filteredAudit.length === 0 ? (
              <div style={emptyMsg}>{t('no_activity')}</div>
            ) : (
              filteredAudit.map((entry, idx, arr) => (
                <div
                  key={entry.id}
                  style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', borderBottom: idx < arr.length - 1 ? '1px solid #f1f4f6' : 'none' }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '14px', color: '#1f2937', marginBottom: '4px' }}>
                      {formatEventType(entry.eventType)}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', lineHeight: '1.4' }}>
                      {entry.actor ? t('apt', { apartment: entry.actor.apartment }) : t('system')}
                      {entry.target ? ` · ${entry.target.levelLabel} ${entry.target.spotNumber}` : ''}
                    </div>
                  </div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', flexShrink: 0, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {safeFormat(entry.timestamp, 'd MMM, HH:mm')}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Show more — allow loading earlier audit logs by extending time range */}
          {!auditSearch && auditHours < 7 * 24 && (
            <button
              onClick={() => setAuditHours((h) => h + 24)}
              disabled={auditQ.isFetching}
              style={{ width: '100%', padding: '14px', borderRadius: '14px', border: 'none', backgroundColor: '#fff', color: '#005bc0', fontWeight: 600, fontSize: '14px', cursor: 'pointer', boxShadow: '0 2px 12px rgba(43,52,55,0.06)', marginBottom: '8px' }}
            >
              {auditQ.isFetching ? t('loading') : t('show_more_past', { hours: auditHours + 24 })}
            </button>
          )}
        </div>

        {/* ───── Danger Zone ───── */}
        <div style={{ marginTop: '24px', paddingTop: '20px', borderTop: '2px solid #f1f4f6' }}>
          <div style={{ ...card, border: '1px solid rgba(220,38,38,0.12)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
              <span className="material-symbols-outlined" style={{ fontSize: '18px', color: '#dc2626' }}>warning</span>
              <span style={{ ...label, color: '#dc2626' }}>{t('danger_zone')}</span>
            </div>

            {/* Building name */}
            <div style={{ marginBottom: '16px' }}>
              <label style={fieldLabel}>{t('building_name')}</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  value={buildingName}
                  onChange={(e) => setBuildingName(e.target.value)}
                  placeholder={t('building_name_placeholder')}
                  style={fieldInput}
                />
                <button
                  onClick={() => patchNameMutation.mutate(buildingName)}
                  disabled={patchNameMutation.isPending || !buildingName.trim() || buildingName === user?.building.name}
                  style={saveBtn}
                >
                  {patchNameMutation.isPending ? '…' : nameSuccess ? '✓' : t('save')}
                </button>
              </div>
              {patchNameMutation.isError && (
                <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>{t('failed_update')}</div>
              )}
            </div>

            {/* Timezone */}
            <div>
              <label style={fieldLabel}>{t('timezone')}</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <select
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  style={{ ...fieldInput, flex: 1 }}
                >
                  {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
                </select>
                <button
                  onClick={() => patchTzMutation.mutate(timezone)}
                  disabled={patchTzMutation.isPending || timezone === user?.building.timezone}
                  style={saveBtn}
                >
                  {patchTzMutation.isPending ? '…' : tzSuccess ? '✓' : t('save')}
                </button>
              </div>
              {patchTzMutation.isError && (
                <div style={{ fontSize: '12px', color: '#dc2626', marginTop: '4px' }}>{t('failed_update')}</div>
              )}
            </div>
          </div>
        </div>
      </div>

      <BottomNav />
      </div>
    </>
  );
}

// ─── shared styles ────────────────────────────────────────────────────────────

const card: React.CSSProperties = {
  backgroundColor: '#ffffff',
  borderRadius:    '20px',
  boxShadow:       '0 2px 12px rgba(43,52,55,0.06)',
  padding:         '20px',
  marginBottom:    '16px',
};

const label: React.CSSProperties = {
  fontSize:      '11px',
  fontWeight:    700,
  textTransform: 'uppercase',
  letterSpacing: '0.08em',
  color:         '#6b7280',
};

const statBox: React.CSSProperties = {
  backgroundColor: '#f8f9fa',
  borderRadius:    '14px',
  padding:         '16px',
};

const statNum: React.CSSProperties = {
  fontFamily:  'Manrope, system-ui',
  fontSize:    '28px',
  fontWeight:  700,
  color:       '#2b3437',
  lineHeight:  1,
};

const statSub: React.CSSProperties = {
  fontSize:      '10px',
  fontWeight:    700,
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  color:         '#9ca3af',
  marginTop:     '4px',
};

const emptyMsg: React.CSSProperties = {
  textAlign: 'center',
  color:     '#9ca3af',
  padding:   '28px 0',
  fontSize:  '14px',
};

const fieldLabel: React.CSSProperties = {
  display:      'block',
  fontSize:     '12px',
  fontWeight:   600,
  color:        '#374151',
  marginBottom: '6px',
};

const fieldInput: React.CSSProperties = {
  flex:            1,
  height:          '38px',
  borderRadius:    '8px',
  border:          '1px solid rgba(43,52,55,0.12)',
  padding:         '0 10px',
  fontSize:        '14px',
  backgroundColor: '#ffffff',
  minWidth:        0,
  boxSizing:       'border-box' as const,
};

const saveBtn: React.CSSProperties = {
  height:       '38px',
  padding:      '0 14px',
  borderRadius: '8px',
  border:       'none',
  background:   'linear-gradient(135deg, #005bc0, #004fa9)',
  color:        '#f7f7ff',
  fontSize:     '13px',
  fontWeight:   600,
  cursor:       'pointer',
  flexShrink:   0,
};

const iconBtn: React.CSSProperties = {
  background: 'none',
  border:     'none',
  cursor:     'pointer',
  color:      '#6b7280',
  padding:    '8px',
  display:    'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const sectionLabel: React.CSSProperties = {
  fontSize:      '12px',
  fontWeight:    600,
  color:         '#9ca3af',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
  marginBottom:  '6px',
};
